// src/compoments/StreetViewSelector.js
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Slider,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useData } from "../context/DataContext";
import theme from "../styles/themes/theme";
const StreetViewSelector = ({ open, onClose, onImageSelect }) => {
  const [address, setAddress] = useState("");
  const [streetViewImage, setStreetViewImage] = useState(null);
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [placeId, setPlaceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(
    !!window.google?.maps
  );
  useEffect(() => {
    if (window.google?.maps) {
      setIsGoogleMapsLoaded(true);
      return;
    }
    const interval = setInterval(() => {
      if (window.google?.maps) {
        setIsGoogleMapsLoaded(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);
  const { setSnackbar } = useData();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  useEffect(() => {
    if (!open || !isGoogleMapsLoaded || !window.google) return;
    let autocomplete;
    const initializeAutocomplete = () => {
      if (!inputRef.current) {
        setTimeout(initializeAutocomplete, 100);
        return;
      }
      autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["address"],
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place && place.place_id) {
          setAddress(place.formatted_address || inputRef.current.value);
          setPlaceId(place.place_id);
        }
      });

      setTimeout(() => {
        const pacContainers = document.getElementsByClassName("pac-container");
        for (let container of pacContainers) {
          container.style.zIndex = "1400";
        }
      }, 500);
    };

    initializeAutocomplete();

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [open, isGoogleMapsLoaded]);
  useEffect(() => {
    if (!placeId || !window.google) return;
    setLoading(true);
    setError(null);

    const streetViewService = new window.google.maps.StreetViewService();
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        streetViewService.getPanorama(
          { location, radius: 50 },
          (data, panoramaStatus) => {
            setLoading(false);
            if (panoramaStatus === "OK") {
              const url = `https://maps.googleapis.com/maps/api/streetview?size=1000x500&location=${location.lat()},${location.lng()}&heading=${heading}&pitch=${pitch}&key=${apiKey}&cb=${Date.now()}`;
              setStreetViewImage(url);
            } else {
              setError("No Street View available for this location.");
            }
          }
        );
      } else {
        setLoading(false);
        setError("Failed to geocode address.");
      }
    });
  }, [placeId, heading, pitch, apiKey]);
  const handleSubmit = () => {
    if (streetViewImage) {
      onImageSelect(streetViewImage);
      setSnackbar({
        open: true,
        message: "You can drag, scale, lock and deselect for best effect.",
        severity: "success",
      });
      onClose();
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        },
      }}
      aria-labelledby="street-view-dialog-title"
      aria-describedby="street-view-dialog-content"
    >
      <DialogTitle
        id="street-view-dialog-title"
        sx={{
          fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          padding: {
            xs: theme.spacing(1),
            sm: theme.spacing(2),
            md: theme.spacing(3),
          },
        }}
      >
        Select Street View
      </DialogTitle>
      <DialogContent id="street-view-dialog-content">
        <TextField
          inputRef={inputRef}
          label="Enter Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          fullWidth
          sx={{
            margin: {
              xs: theme.spacing(0.5),
              sm: theme.spacing(1),
              md: theme.spacing(2),
            },
          }}
          inputProps={{
            autoComplete: "off",
            "aria-label": "Enter address for Street View",
          }}
        />
        {loading && (
          <CircularProgress
            size={{ xs: "1.5rem", sm: "2rem", md: "2.5rem" }}
            sx={{ margin: theme.spacing(1) }}
          />
        )}
        {error && (
          <Typography
            color="error"
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
              margin: theme.spacing(1),
            }}
          >
            {error}
          </Typography>
        )}
        {streetViewImage && (
          <Box
            sx={{
              mt: {
                xs: theme.spacing(1),
                sm: theme.spacing(2),
                md: theme.spacing(3),
              },
            }}
          >
            <img
              src={streetViewImage}
              alt="Street View preview"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: { xs: "15rem", sm: "20rem", md: "25rem" },
                objectFit: "contain",
              }}
            />
            <Box
              sx={{
                mt: {
                  xs: theme.spacing(1),
                  sm: theme.spacing(2),
                  md: theme.spacing(3),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Heading: {heading}°
              </Typography>
              <Slider
                value={heading}
                onChange={(e, value) => setHeading(value)}
                min={0}
                max={360}
                step={1}
                aria-label="Adjust Street View heading"
                sx={{
                  margin: theme.spacing(0, { xs: 0.5, sm: 1, md: 2 }),
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Pitch: {pitch}°
              </Typography>
              <Slider
                value={pitch}
                onChange={(e, value) => setPitch(value)}
                min={-90}
                max={90}
                step={1}
                aria-label="Adjust Street View pitch"
                sx={{
                  margin: {
                    xs: theme.spacing(0.5),
                    sm: theme.spacing(1),
                    md: theme.spacing(2),
                  },
                }}
              />
            </Box>
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!streetViewImage || loading}
          sx={{
            mt: {
              xs: theme.spacing(1),
              sm: theme.spacing(2),
              md: theme.spacing(3),
            },
            height: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          }}
          aria-label="Add Street View image to canvas"
        >
          Add to Canvas
        </Button>
      </DialogContent>
    </Dialog>
  );
};
export default StreetViewSelector;
